import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useDebounce } from 'src/components/helpers/useDebounce';
import { CasesForLinkingQuery } from 'src/graphql';
import { useSmartSpinnerLoading } from 'src/hooks/useSmartSpinnerLoading';
import { fetcher } from 'src/utils/fetch';

const SearchCasesForLinkingDocument = `
  query SearchCasesForLinking(
    $search: String!
    $caseId: String!
    $limit: Int
    $offset: Int
  ) {
    searchCasesForLinking(
      search: $search
      caseId: $caseId
      limit: $limit
      offset: $offset
    ) {
      count
      items {
        id
        name
        updatedAt
        readableId
        status
      }
    }
  }
`;

const SEARCH_DEBOUNCE_MS = 350;

// Temporary type until codegen runs
type SearchCasesForLinkingQuery = {
  searchCasesForLinking: CasesForLinkingQuery['casesAbleToBeLinkedToCase'];
};

type SearchCasesForLinkingQueryVariables = {
  search: string;
  caseId: string;
  limit?: number;
  offset?: number;
};

interface UseCaseLinkSearchOptions {
  caseId: string;
  enabled: boolean;
}

interface UseCaseLinkSearchResult {
  searchValue: string;
  setSearchValue: (value: string) => void;
  displayedResults: CasesForLinkingQuery['casesAbleToBeLinkedToCase'] | null;
  showLoader: boolean;
  hasSearchQuery: boolean;
}

export const useCaseLinkSearch = ({
  caseId,
  enabled,
}: UseCaseLinkSearchOptions): UseCaseLinkSearchResult => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [displayedResults, setDisplayedResults] = useState<
    CasesForLinkingQuery['casesAbleToBeLinkedToCase'] | null
  >(null);

  // Debounce search input to avoid firing requests on every keystroke
  const debouncedSearch = useDebounce(() => {
    const trimmedValue = searchValue.trim();
    if (trimmedValue.length > 0) {
      setDebouncedSearchValue(trimmedValue);
    } else {
      setDebouncedSearchValue('');
      setDisplayedResults(null);
    }
  }, SEARCH_DEBOUNCE_MS);

  // Reset search state when modal closes
  useEffect(() => {
    if (!enabled) {
      setSearchValue('');
      setDebouncedSearchValue('');
      setDisplayedResults(null);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      debouncedSearch();
    }
  }, [searchValue, enabled, debouncedSearch]);

  const { data, isLoading, isFetching } = useQuery<
    SearchCasesForLinkingQuery,
    unknown,
    SearchCasesForLinkingQuery
  >(
    ['SearchCasesForLinking', { search: debouncedSearchValue, caseId }],
    fetcher<SearchCasesForLinkingQuery, SearchCasesForLinkingQueryVariables>(
      SearchCasesForLinkingDocument,
      {
        search: debouncedSearchValue,
        caseId,
        limit: 10,
        offset: 0,
      },
    ),
    {
      enabled: enabled && debouncedSearchValue.trim().length > 0,
      refetchOnWindowFocus: false,
    },
  );

  // Update displayed results only when query completes successfully
  useEffect(() => {
    if (
      data?.searchCasesForLinking &&
      !isFetching &&
      !isLoading &&
      debouncedSearchValue.trim().length > 0
    ) {
      setDisplayedResults(data.searchCasesForLinking);
    }
  }, [data, debouncedSearchValue, isFetching, isLoading]);

  // Smart spinner: only shows for requests > 180ms, stays visible for min 300ms
  const showLoader = useSmartSpinnerLoading({ isLoading, isFetching });
  const hasSearchQuery = debouncedSearchValue.trim().length > 0;

  return {
    searchValue,
    setSearchValue,
    displayedResults,
    showLoader,
    hasSearchQuery,
  };
};
